import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import { BoundedContent, GridContainer, Row, Column } from '@vp/swan'
import { useStyles, useSwanStyleKeys } from '@vp/ubik-context'

import { stylesheet } from 'client/styles.scss'
import { PreviewArea } from 'client/components/Gallery/PreviewArea'
import { FiltersSidebar } from 'client/components/Gallery/Filters/FiltersSidebar'
import { Pagination } from 'client/components/Gallery/Pagination'
import { Subheader } from 'client/components/Gallery/Subheader'
import { HeroHeader } from 'client/components/Gallery/Header/HeroHeader'
import { Header } from 'client/components/Gallery/Header'
import { booleanRenderPropertySelector, getMetadata, getIGPL0Url } from 'client/store/config/reducer'
import { RenderProperty } from 'shared/renderProperties'
import { SecondaryContent } from '~/client/components/Gallery/SecondaryContent'
import { Faq } from '~/client/components/Gallery/Footer/Faq'
import { ProductOptionsDescriptions } from '~/client/components/Gallery/Footer/ProductOptionsDescriptions'
import { QuickLinks } from '~/client/components/Gallery/Footer/QuickLinks'
import { IGPL0Callout } from '~/client/components/Gallery/Footer/IGPL0Callout'
import { useInitExperiments } from 'client/components/Gallery/hooks/useInitExperiments'
import { DesignPersonalizationProvider } from './DesignPersonalization'
import { ImageLoadedInstrumenter } from '../common/ImageLoadedInstrumenter'
import { useUpdateAuthData } from '~/client/hooks/useUpdateAuthData'
import { SetHighlightingProvider } from '~/client/components/Gallery/SetHighlighting/SetHighlightingContext'
import { ProductStructuredData } from 'client/components/Gallery/ProductStructuredData'
import { SWAN_STYLE_KEYS } from 'client/components/FragmentRoot/constants'
import { useInitDebug } from './hooks/useInitDebug'
import { useInitTracking } from '~/client/components/Gallery/hooks/useInitTracking'
import { getDebugModeEnabled } from '~/client/store/debug'

const DebugToolbar = lazy(() => import('client/components/DebugToolbar'))

export const Gallery = () => {
  const galleryMetadata = useSelector(getMetadata)
  const booleanRenderProperty = useSelector(booleanRenderPropertySelector)
  const debugEnabled = useSelector(getDebugModeEnabled)
  const igpL0Url = useSelector(getIGPL0Url)

  // Render props
  const showProductInfo = booleanRenderProperty(RenderProperty.ShowProductInfo)
  const showIGPL0Url = booleanRenderProperty(RenderProperty.ShowTLPL0Quicklink)
  const showQuicklinks = booleanRenderProperty(RenderProperty.ShowQuicklinks)

  const shouldShowIgp0Callout = !showQuicklinks && showIGPL0Url && igpL0Url

  useSwanStyleKeys(SWAN_STYLE_KEYS)
  useStyles(stylesheet)

  useInitTracking()
  useInitExperiments()
  useUpdateAuthData()
  useInitDebug()

  return (
    <>
      <ProductStructuredData />
      <BoundedContent className="app">
        <ImageLoadedInstrumenter />
        <SetHighlightingProvider>
          <DesignPersonalizationProvider>
            <GridContainer>
              <Row>
                <Column span={12}>
                  <HeroHeader />
                  <Header />
                </Column>
              </Row>
              <Row>
                <Column className="left-nav-wrapper" span={2} spanLg={3} spanMd={3} spanSm={4} spanXl={2} spanXs={null}>
                  <FiltersSidebar />
                </Column>
                <Column span={10} spanLg={9} spanMd={9} spanSm={8} spanXl={10} spanXs={12}>
                  <Subheader />
                  <PreviewArea />
                  <Pagination />
                  {!!galleryMetadata && showProductInfo && <SecondaryContent />}
                  <section className="footer">
                    <Faq />
                  </section>
                  <nav className="external-links">
                    <ProductOptionsDescriptions />
                    <QuickLinks />
                    {shouldShowIgp0Callout && <IGPL0Callout />}
                  </nav>
                </Column>
              </Row>
              {debugEnabled && (
                <Suspense fallback={null}>
                  <DebugToolbar />
                </Suspense>
              )}
            </GridContainer>
          </DesignPersonalizationProvider>
        </SetHighlightingProvider>
      </BoundedContent>
    </>
  )
}

Gallery.displayName = 'Gallery'
